'use client';

import FloatingContainer from '@/components/base/floating/floating-container';
import useCommonFloating from '@/components/base/floating/use-common-floating';
import {
  type Placement,
  useDismiss,
  useInteractions,
} from '@floating-ui/react';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';

interface Props {
  placement?: Placement;
  children?: ReactNode;
  Content?: ReactNode;
  Header?: ReactNode;
  isOpen?: boolean;
  setIsOpen?: (newOpenState: boolean) => void;
  enableDismiss?: boolean;
  offset?: number;
  variant?: 'dark' | 'light';
  className?: string;
  classNameSection?: string;
  showCloseButton?: boolean;
  closeModal?: () => void;
}

const Popover = ({
  placement,
  children,
  Content,
  Header,
  isOpen,
  setIsOpen,
  enableDismiss,
  offset = 0,
  className,
  classNameSection,
  showCloseButton,
  closeModal,
  variant = 'dark',
}: Props) => {
  const { refs, transitionRef, context, mounted, ...containerProps } =
    useCommonFloating({
      placement,
      offset,
      isOpen,
      setIsOpen,
    });

  const dismiss = useDismiss(context);
  const interactions = enableDismiss ? [dismiss] : [];
  const { getReferenceProps, getFloatingProps } = useInteractions(interactions);

  return (
    <div className={classNameSection}>
      <span ref={refs.setReference} {...getReferenceProps()}>
        {children}
      </span>
      {mounted && (
        <FloatingContainer
          ref={refs.setFloating}
          transitionRef={transitionRef}
          className={clsx('p-inner', className)}
          variant={variant}
          showCloseButton={showCloseButton}
          closeModal={closeModal}
          context={context}
          Header={Header}
          {...containerProps}
          {...getFloatingProps()}
        >
          {Content}
        </FloatingContainer>
      )}
    </div>
  );
};

export default Popover;
